"use client";

import React from "react";
import { motion } from "framer-motion";
import { Compass, Search, TrendingUp, Eye, ChartBar as BarChart3, Sparkles, ArrowRight, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
const capabilitiesList = [{
  id: "keywords",
  category: "Search Intent",
  title: "Keyword Research",
  description: "Finding high-intent, achievable search terms that drive qualified traffic and attract visitors ready to buy directly to your key pages.",
  iconName: "Search",
  metricLabel: "Core Capability"
}, {
  id: "technical",
  category: "Site Health & Crawling",
  title: "Technical SEO Audits",
  description: "Catching crawl errors, broken links, redirect chains, and indexing issues before they can hurt your search rankings and visibility.",
  iconName: "Compass",
  metricLabel: "Core Capability"
}, {
  id: "onpage",
  category: "Content & Structure",
  title: "On-Page & Content Optimisation",
  description: "Improving page content, metadata, and site architecture to satisfy search intent and communicate clear relevance to search engines.",
  iconName: "BarChart3",
  metricLabel: "Core Capability"
}, {
  id: "tracking",
  category: "Position Monitoring",
  title: "Rank Tracking",
  description: "Monitoring search positions across desktop and mobile devices so you can see ranking movement and SERP fluctuations as they happen.",
  iconName: "TrendingUp",
  metricLabel: "Core Capability"
}, {
  id: "competitors",
  category: "Market Intelligence",
  title: "Competitor Analysis",
  description: "Uncovering keyword gaps, link profiles, and high-value content opportunities that your direct market competitors are currently missing.",
  iconName: "Eye",
  metricLabel: "Core Capability"
}];
function getCapabilityIcon(iconName: string) {
  switch (iconName) {
    case "Search":
      return <Search className="size-6 text-primary shrink-0" />;
    case "Compass":
      return <Compass className="size-6 text-primary shrink-0" />;
    case "BarChart3":
      return <BarChart3 className="size-6 text-primary shrink-0" />;
    case "TrendingUp":
      return <TrendingUp className="size-6 text-primary shrink-0" />;
    case "Eye":
      return <Eye className="size-6 text-primary shrink-0" />;
    default:
      return <Sparkles className="size-6 text-primary shrink-0" />;
  }
}
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.12
    }
  }
};
const itemVariants = {
  hidden: {
    opacity: 0,
    y: 20
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: "easeOut"
    }
  }
};
export default function Criteria() {
  return <section id="buyercriteriafeaturegrid" data-nav="light" className="bg-background text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-8 border-b border-border">
      <div className="max-w-7xl mx-auto space-y-16">
        {/* Section Header */}
        <div className="text-center max-w-3xl mx-auto space-y-4">
          <Badge variant="secondary" className="px-3.5 py-1 text-xs font-semibold rounded-full uppercase tracking-wider text-primary bg-secondary">
            <Sparkles className="size-3.5 mr-1" /> What DIY SEO Software Does
          </Badge>
          <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-foreground">
            The Core Capabilities of DIY SEO Software
          </h2>
          <p className="font-sans text-muted-foreground text-base sm:text-lg leading-relaxed">
            While I often build bespoke search strategies for ambitious businesses, dedicated DIY tools empower you to manage fundamental organic tasks independently. Here is what reliable DIY SEO software should deliver.
          </p>
        </div>

        {/* Criteria Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {capabilitiesList.map((item, i) => <motion.div key={item.id} data-index={i} variants={itemVariants} className={`h-full ${i === 3 ? "lg:col-start-1" : ""} ${i === 4 ? "md:col-span-2 lg:col-span-1 md:max-w-md md:mx-auto lg:max-w-none w-full" : ""}`}>
              <Card className="h-full bg-card border border-border shadow-sm rounded-xl p-6 sm:p-8 hover:shadow-md transition-all duration-300 flex flex-col justify-between group">
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <div className="size-12 rounded-full bg-secondary flex items-center justify-center transition-transform duration-300 group-hover:scale-105 shadow-2xs">
                      {getCapabilityIcon(item.iconName)}
                    </div>
                    <Badge variant="outline" className="text-xs font-medium text-muted-foreground border-border rounded-md px-2.5 py-0.5">
                      {item.category}
                    </Badge>
                  </div>

                  <CardHeader className="p-0 mb-3">
                    <CardTitle className="font-serif text-xl font-bold text-foreground leading-snug">
                      {item.title}
                    </CardTitle>
                  </CardHeader>

                  <CardContent className="p-0">
                    <CardDescription className="font-sans text-muted-foreground text-sm sm:text-base leading-relaxed">
                      {item.description}
                    </CardDescription>
                  </CardContent>
                </div>

                <div className="mt-6 pt-4 border-t border-border/60 flex items-center text-xs font-semibold text-primary group-hover:translate-x-1 transition-transform duration-200">
                  <span>{item.metricLabel}</span>
                  <CheckCircle2 className="size-3.5 ml-1.5" />
                </div>
              </Card>
            </motion.div>)}
        </motion.div>

        {/* Bottom Consultation Banner */}
        <div className="bg-card border border-border rounded-2xl p-8 sm:p-10 shadow-sm flex flex-col md:flex-row items-center justify-between gap-6 max-w-5xl mx-auto">
          <div className="space-y-2 text-center md:text-left">
            <h3 className="font-serif text-xl sm:text-2xl font-bold text-foreground">
              Need personalised guidance instead of DIY software?
            </h3>
            <p className="font-sans text-muted-foreground text-sm sm:text-base">
              Unsure whether to handle SEO in-house with DIY software or hire an independent consultant? Let's evaluate your internal capacity, technical complexity, and which path delivers the best return.
            </p>
          </div>
          <div className="flex items-center gap-4 shrink-0">
            <Button asChild size="lg" className="rounded-lg shadow-sm">
              <Link to="/ContactPage" className="inline-flex items-center gap-2">
                Book a Free Consultation
                <ArrowRight className="size-4" />
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}